<template>
<div class="chapter">
<div class="inner_page">
<div class="chapter" title="Introduction">
<div class="titlepage">
<div>
<div>
<h1 class="title"><a id="d0e35" xmlns:saxon="http://icl.com/saxon">Introduction </a></h1><a id="d0e35" xmlns:saxon="http://icl.com/saxon">
</a></div><a id="d0e35" xmlns:saxon="http://icl.com/saxon">
</a></div><a id="d0e35" xmlns:saxon="http://icl.com/saxon">
</a></div><a id="d0e35" xmlns:saxon="http://icl.com/saxon">
<p>Now is the time to examine your food budget and develop savvy shopping and cooking skills
        that will benefit you and your whole family. This book is full of tips and advice on how to
        save money on food—one of the few areas of spending you can actually control. Besides
        providing you with budget-saving techniques, this book also offers 100 imaginative recipes
        made from low-cost ingredients that will please anyone who tries them. Let the saving begin! </p>
</a><h4><a id="d0e35" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e40" xmlns:saxon="http://icl.com/saxon">Have a Plan Before You Shop </a></h4><a id="d0e40" xmlns:saxon="http://icl.com/saxon">
<p>The key to staying within your food budget is to have a plan. In the case of grocery
        shopping, your plan should include a grocery list. Once you have a list, stick to it! Check
        out the following suggestions for creating the perfect grocery-shopping list. </p>
<p><span class="bold">
<strong>1. </strong>
</span>Do you know what’s on sale? Local grocery stores typically send out fliers in the
        mail or newspaper each week. Take advantage of these fliers and use them to help make your
        weekly grocery list. Start your list by writing down the store, sale item, and price. After
        you’ve put your list together (including ingredients from the recipes you will be making),
        review it. Is there something you can eliminate? After a few weeks of smart shopping and
        thrifty cooking, you’ll start to see places on your grocery list where you can really cut
        back. </p>
<p><span class="bold">
<strong>NOTE: </strong>
</span>Smart spenders know that the key to the greatest savings depends on purchasing the
        best sale items. Combined with coupons, the grocery items on sale can be purchased at
        greatly reduced prices. </p>
<p><span class="bold">
<strong>2. </strong>
</span>Look ahead a week or two when making out your shopping list. This means planning out
        the recipes you intend to make ahead of time as well. In your planning, consider what you
        already have in your pantry, refrigerator, or freezer that could be used in combination
        with, or as a substitute for, ingredients in upcoming recipes you want to make. This method
        will prevent you from buying unnecessary groceries and will also help to use up any
        leftovers that might be looming in your kitchen. </p>
<p><span class="bold">
<strong>3. </strong>
</span>Many ingredients are common in multiple recipes, so consider looking for recipes to
        make each week that share some of the same ingredients but are different enough to provide
        delicious variety for your family. Also, think about recipes that would be easy to double
        and freeze for later use. </p>
<p><span class="bold">
<strong>4. </strong>
</span>Where is the best place to shop? Most people go to the supermarket or store that is
        the closest or most convenient for them. Since you’ve already made a grocery list of stores
        and sale items, try to stick to the list and shop at those places instead. However, keep in
        mind that it isn’t worth spending a dollar in gas to save twenty-five cents on a single food
        item. </p>
<p><span class="bold">
<strong>NOTE: </strong>
</span>Some grocery stores offer price matching for sale items. Just ask the customer
        service desk if they offer the service and what you will need to do to benefit from it. </p>
<p><span class="bold">
<strong>5. </strong>
</span>Clipping coupons really does cut costs. In short, some people are meant to be coupon
        clippers and some are not. While some find it frustrating and time- consuming to clip
        coupons for everything, there are those who do it and love it. Just remember to clip coupons
        for things you normally buy; just because you have a coupon for something doesn’t mean you
        have to buy it. If it’s something out of the ordinary, getting it at a reduced price still
        means you’re spending outside of your budget to get it. </p>
<p><span class="bold">
<strong>NOTE: </strong>
</span>Many stores have double-coupon days. This means the store matches the discount on the
        coupon and you save double the amount! </p>
</a><h4><a id="d0e40" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e76" xmlns:saxon="http://icl.com/saxon">General Grocery Shopping Tips </a></h4><a id="d0e76" xmlns:saxon="http://icl.com/saxon">
<p>Even if you are armed with the best-ever shopping list, it’s tough to get in and out of the
        store as planned without a whole lot of will power. Things such as store layout, time of
        day, your state of mind, and really powerful marketing techniques are at work to get you to
        spend more money. For those who are new to food budgeting, this might all seem a bit
        overwhelming. However, with a few weeks of practice, you’ll find that you’re planning meals
        and shopping like a pro. The following tips on shopping in general may help guide you in
        your quest to save money. </p>
<p><span class="bold">
<strong>• </strong>
</span>Try to choose a time when you can go to the store with the fewest distractions,
        whether that means leaving kids at home or avoiding crowds of other shoppers. Try going
        early in the morning or late at night. Generally, avoid the after-work rush and anytime
        after 11:00 a.m. on weekends. </p>
<p><span class="bold">
<strong>• </strong>
</span>Go to the grocery store as infrequently as you can. Try going once a week to coincide
        with the weekly fliers. The easiest way to blow your food budget is to go the store multiple
        times each week for a few items, because most likely you’ll leave the store with more than
        you intended. If you can make do with what you have at home, you will save money every
        month. </p>
<p><span class="bold">
<strong>• </strong>
</span>Don’t shop when you’re hungry or thirsty. If this can’t be avoided, try drinking
        water before you go shopping to take the edge off your appetite. </p>
<p><span class="bold">
<strong>• </strong>
</span>When walking down the aisles, look low and high for the less expensive brands. Most
        stores place the brand-name products on eye-level shelves because shoppers tend to grab
        whatever is in front of them instead of looking for the best bargains. </p>
<p><span class="bold">
<strong>• </strong>
</span>Stay focused on the price of the item, not the packaging it comes in. Since bright
        colors and fancy packaging are attractive, shoppers tend to pick up these items first. House
        brands are generally less colorful but typically less expensive. </p>
<p><span class="bold">
<strong>• </strong>
</span>Remember to stick to your list to avoid impulse buying. When you pick up something
        that isn’t on your list, ask yourself why you want it. Do you really <span class="emphasis">
<em>need</em>
</span> it or do you just <span class="emphasis">
<em>want</em>
</span> it? Too many impulse purchases can ruin your carefully planned food budget. </p>
<p><span class="bold">
<strong>• </strong>
</span>It is a huge advantage to know which fruits and vegetables are in season, as they
        will be part of some great sales. Not only are in-season produce items less expensive,
        they’re fresher. It’s also wise to learn which vegetables and fruits are less expensive when
        fresh, and which ones are less expensive when frozen or canned. </p>
<p><span class="bold">
<strong>NOTE: </strong>
</span>Beware of the conveniently packaged produce designed to save you time in preparation.
        Most of these convenience items cost twice as much. </p>
<p><span class="bold">
<strong>• </strong>
</span>Remember to check the weekly fliers for sales on your family’s favorite snack foods.
        Everyone loves snacks, and it’s easy to spend a lot on salty and sugary items if they aren’t
        on sale. </p>
<p><span class="bold">
<strong>• </strong>
</span>The frozen food section is the home of some of the best grocery bargains, as well as
        some of the worst. Remember, convenience often comes with a price. On the flip side, there
        are some frozen foods that cost much less than fresh or canned, and they are often healthier
        because the vitamin content is preserved when they are flash frozen. </p>
</a><h4><a id="d0e76" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e126" xmlns:saxon="http://icl.com/saxon">20 Budget-Saving Tips </a></h4><a id="d0e126" xmlns:saxon="http://icl.com/saxon">
<p>The following list includes the best budget-saving tips in a nutshell. </p>
<p><span class="bold">
<strong>1. </strong>
</span>Use coupons. Though this doesn’t really need explanation, clipping coupons is an
        excellent way to save. See if the grocer in your area has a double- or triple-coupon day for
        even greater savings. </p>
<p><span class="bold">
<strong>2. </strong>
</span>Know your family’s likes and dislikes. Stock up on favorites when they are on sale
        and skip sales of items your family doesn’t like. </p>
<p><span class="bold">
<strong>3. </strong>
</span>Look for recipes that require a few inexpensive ingredients and avoid those that
        require you to buy something you’ll rarely use again, such as an exotic spice. </p>
<p><span class="bold">
<strong>4. </strong>
</span>Experiment with recipes. If you’re making a dessert that calls for a spice you don’t
        have, try to find a substitute spice in your pantry. </p>
<p><span class="bold">
<strong>5. </strong>
</span>Substitute less-expensive ingredients for expensive ones. Determine if the recipe
        absolutely must have that item to make it work. If not, leave it out altogether or use a
        cheaper alternative. For example, using margarine instead of butter works in many recipes. </p>
<p><span class="bold">
<strong>6. </strong>
</span>Use less of an expensive ingredient if you can’t find a substitute for it. For
        example, try cutting the amount by a third. Keep cutting each time you make the recipe to
        see how it tastes. Once the adjustment is noticeable, you know you’ve cut too much. </p>
<p><span class="bold">
<strong>7. </strong>
</span>Buy store brands. These brands are generally less expensive and usually just as good
        as name brands—sometimes even better. </p>
<p><span class="bold">
<strong>8. </strong>
</span>Think of ways to use everything you already have. Maybe you will create an amazing
        new dessert! </p>
<p><span class="bold">
<strong>9. </strong>
</span>Substitute canned fruit for fresh if a recipe calls for a fruit that is not in season
        or when it won’t sacrifice taste. </p>
<p><span class="bold">
<strong>10. </strong>
</span>Save or bookmark recipes that your family really likes so you know what ingredients
        to stock in your pantry. </p>
<p><span class="bold">
<strong>11. </strong>
</span>Try to make desserts that will have leftovers so you can enjoy it several nights of
        the week. </p>
<p><span class="bold">
<strong>12. </strong>
</span>Have a tried-and-true “standby” dessert for which the ingredients are always in your
        pantry, such as a favorite cake or yummy cookies. </p>
<p><span class="bold">
<strong>13. </strong>
</span>Buy in bulk. Though the upfront cost is more, you’ll be able to get more uses out of
        bulk item purchases, making the actual costs per serving much less. </p>
<p><span class="bold">
<strong>14. </strong>
</span>Freezing is a great way to preserve the quality of foods until you can use them,
        especially if you’re buying in bulk. Store items such as fruits or vegetables in 1-cup
        portions so they are easy to grab. </p>
<p><span class="bold">
<strong>15. </strong>
</span>Invest in basic food storage items. Simple plastic containers with lids, freezer
        bags, and aluminum foil can keep food fresh and protected until you need it again. </p>
<p><span class="bold">
<strong>16. </strong>
</span>Cut down on or eliminate junk food. Find healthier and less-expensive alternatives
        instead. </p>
<p><span class="bold">
<strong>17. </strong>
</span>Don’t order dessert at restaurants. You can make a great treat at home for much less!
        Even better, don’t do to the restaurant for dinner and try something from <span class="emphasis">
<em>100 Meals for $5 or Less</em>
</span> by Jennifer Maughan. </p>
<p><span class="bold">
<strong>18. </strong>
</span>Don’t sacrifice taste on low-cost items. The key is making delicious and nutritious
        soups that satisfy. </p>
<p><span class="bold">
<strong>19. </strong>
</span>Think healthy and implement low-cost yet nutritious foods into your diet. </p>
<p><span class="bold">
<strong>20. </strong>
</span>Control portions. We tend to eat portions that far exceed what we actually need. Once
        you get used to smaller portions, you’ll find that you are satisfied on less. Consult
        nutrition guides for recommended portions and serving sizes. </p>
</a><h4><a id="d0e126" xmlns:saxon="http://icl.com/saxon"></a><a id="d0e213" xmlns:saxon="http://icl.com/saxon">The Well-Stocked Budget Pantry and
        Refrigerator </a></h4><a id="d0e213" xmlns:saxon="http://icl.com/saxon">
<p>When planning for recipes, there are a number of items that a well-stocked budget pantry
        and refrigerator should include. The following list is by no means inclusive, but it does
        list grocery items that are traditionally considered staples and that are inexpensive, store
        well, and are versatile enough to appear in many different dishes without becoming boring.
        Keep track of what ingredients you use most often, and if you see one of the must-have items
        on sale, stock up! </p>
<p>Try to keep these items in your pantry or refrigerator: </p>
<p><span class="bold">
<strong>• </strong>
</span>Biscuit baking mixes </p>
<p><span class="bold">
<strong>• </strong>
</span>Breads </p>
<p><span class="bold">
<strong>• </strong>
</span>Canned cream soups </p>
<p><span class="bold">
<strong>• </strong>
</span>Canned vegetables </p>
<p><span class="bold">
<strong>• </strong>
</span>Cereals </p>
<p><span class="bold">
<strong>• </strong>
</span>Cheeses </p>
<p><span class="bold">
<strong>• </strong>
</span>Chips and crackers </p>
<p><span class="bold">
<strong>• </strong>
</span>Condiments </p>
<p><span class="bold">
<strong>• </strong>
</span>Cornmeal </p>
<p><span class="bold">
<strong>• </strong>
</span>Cream, condensed milk, and evaporated milk </p>
<p><span class="bold">
<strong>• </strong>
</span>Dry beans and canned beans </p>
<p><span class="bold">
<strong>• </strong>
</span>Eggs </p>
<p><span class="bold">
<strong>• </strong>
</span>Flour </p>
<p><span class="bold">
<strong>• </strong>
</span>Frozen fruit and canned fruit </p>
<p><span class="bold">
<strong>• </strong>
</span>Frozen mixed vegetables </p>
<p><span class="bold">
<strong>• </strong>
</span>Lemon juice </p>
<p><span class="bold">
<strong>• </strong>
</span>Margarine, butter, shortening, and cooking oils </p>
<p><span class="bold">
<strong>• </strong>
</span>Meats </p>
<p><span class="bold">
<strong>• </strong>
</span>Milk </p>
<p><span class="bold">
<strong>• </strong>
</span>Pasta </p>
<p><span class="bold">
<strong>• </strong>
</span>Peanut butter </p>
<p><span class="bold">
<strong>• </strong>
</span>Potatoes </p>
<p><span class="bold">
<strong>• </strong>
</span>Rice </p>
<p><span class="bold">
<strong>• </strong>
</span>Salt, pepper, and spices </p>
<p><span class="bold">
<strong>• </strong>
</span>Sugar (white, brown, and powdered)</p>
</a></div><a id="d0e213" xmlns:saxon="http://icl.com/saxon">
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ch01.vue?af364572